import React, { useEffect, useRef, useState, useMemo } from 'react'
import './eval-data-v2.styles.scss'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getQueryFromFirestore } from '../../../utils/getDataFromDbSimplified';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const TIME_RANGES = [
  { days: 10, label: '10 Days' },
  { days: 30, label: '30 Days' },
  { days: 90, label: '90 Days' },
  { days: 180, label: '180 Days' },
  { days: 365, label: '1 Year' },
  { days: 99999, label: 'All Time' },
];

const EvalDataV2 = ({ evalList }) => {

  const screenSize = useSelector(state => state.listsData.screenSize);
  const userProfile = useSelector(state => state.userData.userProfile);
  const dbData = useSelector(state => state.userData.dbData);
  const [activityCounts, setActivityCounts] = useState(null);
  const [firstRecord, setFirstRecord] = useState(null);
  const [timeRange, setTimeRange] = useState(10);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const sourceChartRef = useRef(null);
  const sourceChartInstance = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
      if (sourceChartInstance.current) sourceChartInstance.current.destroy();
    };
  }, []);

  const availableDays = useMemo(() => {
    if (!firstRecord?.date) return 0;
    const first = new Date(firstRecord.date + 'T00:00:00');
    return Math.floor((Date.now() - first.getTime()) / 86400000);
  }, [firstRecord]);

  useEffect(() => {
    if (!userProfile) return;
    if (!dbData.Admin) navigate('/');
    getFirstActivityRecord();
    getActivityCounts(10);
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile) return;
    getActivityCounts(timeRange);
  }, [timeRange]);

  function getFirstActivityRecord() {
    getQueryFromFirestore({
      'path': 'dailyActivityCounts',
      'orderBy': { 'field': 'date', 'direction': 'asc' },
      'loadLimit': 1,
      'once': true,
      'callback': (data) => {
        setFirstRecord(Object.values(data)[0]);
      }
    });
  }

  function getActivityCounts(days) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const formattedDate = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
    setActivityCounts(null);
    getQueryFromFirestore({
      'path': 'dailyActivityCounts',
      'queryArray': [{ 'field': 'date', 'operator': '>=', 'value': formattedDate }],
      'callback': (data) => setActivityCounts(data),
      'once': true
    });
  }

  const stats = useMemo(() => {
    if (!activityCounts || !evalList) return null;

    const evalTotals = {};
    const dayTotals = {};

    for (const dayRecord of Object.values(activityCounts)) {
      const { counts = {}, date } = dayRecord;
      let daySum = 0;
      for (const [evalId, count] of Object.entries(counts)) {
        evalTotals[evalId] = (evalTotals[evalId] || 0) + count;
        daySum += count;
      }
      if (date) dayTotals[date] = (dayTotals[date] || 0) + daySum;
    }

    const totalCompletions = Object.values(evalTotals).reduce((a, b) => a + b, 0);

    const sortedEvalEntries = Object.entries(evalTotals).sort((a, b) => b[1] - a[1]);
    const [topEvalId, topEvalCount] = sortedEvalEntries[0] || [null, 0];
    const mostCommonEval = topEvalId ? evalList[topEvalId] : null;

    const sourceBreakdown = {};
    for (const [evalId, count] of Object.entries(evalTotals)) {
      const source = evalList[evalId]?.source || 'Unknown';
      sourceBreakdown[source] = (sourceBreakdown[source] || 0) + count;
    }

    let totalHours = 0;
    let completionsWithHours = 0;
    for (const [evalId, count] of Object.entries(evalTotals)) {
      const ev = evalList[evalId];
      if (ev && ev.hours > 0) {
        totalHours += ev.hours * count;
        completionsWithHours += count;
      }
    }

    const sortedDays = Object.entries(dayTotals).sort((a, b) => a[0].localeCompare(b[0]));
    const busiestDay = [...sortedDays].sort((a, b) => b[1] - a[1])[0];

    const top5 = sortedEvalEntries.slice(0, 5).map(([id, count]) => ({
      ev: evalList[id],
      count,
      id,
    }));

    const cpduCount = Object.entries(evalTotals).reduce((sum, [id, count]) => (
      evalList[id]?.cpdu ? sum + count : sum
    ), 0);

    return {
      totalCompletions,
      mostCommonEval,
      topEvalCount,
      sourceBreakdown,
      totalHours: Number(totalHours.toFixed(1)),
      completionsWithHours,
      busiestDay,
      uniqueEvalsCount: Object.keys(evalTotals).length,
      cpduCount,
      sortedDays,
      top5,
    };
  }, [activityCounts, evalList]);

  useEffect(() => {
    if (!stats || !chartRef.current || stats.sortedDays.length === 0) return;

    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: stats.sortedDays.map(([date]) => {
          const parts = date.split('-');
          return `${parts[1]}/${parts[2]}`;
        }),
        datasets: [{
          label: 'Completions',
          data: stats.sortedDays.map(([, total]) => total),
          backgroundColor: 'rgba(1, 91, 154, 0.55)',
          borderColor: 'rgba(1, 91, 154, 1)',
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: 'rgba(1, 91, 154, 0.8)',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.parsed.y} completion${ctx.parsed.y !== 1 ? 's' : ''}`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { stepSize: 1, precision: 0 },
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          x: { grid: { display: false } }
        }
      }
    });
  }, [stats]);

  useEffect(() => {
    if (!stats || !sourceChartRef.current) return;

    if (sourceChartInstance.current) sourceChartInstance.current.destroy();

    const sorted = Object.entries(stats.sourceBreakdown).sort((a, b) => b[1] - a[1]);
    const ctx = sourceChartRef.current.getContext('2d');
    sourceChartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: sorted.map(([source]) => source),
        datasets: [{
          data: sorted.map(([, count]) => count),
          backgroundColor: 'rgba(1, 91, 154, 0.55)',
          borderColor: 'rgba(1, 91, 154, 1)',
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: 'rgba(1, 91, 154, 0.8)',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.parsed.y.toLocaleString()} completion${ctx.parsed.y !== 1 ? 's' : ''}`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { stepSize: 1, precision: 0 },
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          x: { grid: { display: false } }
        }
      }
    });
  }, [stats]);

  return (
    <div className={`eval-data-v2 ${screenSize ? ` ${screenSize}` : ''}`}>
      <div className='eval-data-v2-card g-card'>
        <div className='eval-data-v2__header'>
          <div className='eval-data-v2__header-top'>
            <div>
              <h3>Evaluation Activity</h3>
              <p className='meta'>
                Showing last {timeRange === 365 ? '1 year' : `${timeRange} days`}
                {firstRecord?.date && ` · Tracking started ${firstRecord.date}`}
              </p>
            </div>
            <div className='eval-data-v2__range-buttons'>
              {TIME_RANGES.map(({ days, label }) => (
                <button
                  key={days}
                  className={`g-button small-btn
                  ${timeRange === days ? ' primary' : ''}
                  ${availableDays > 0 && availableDays < days && days !== 99999 ? ' disabled' : ''}`}
                  disabled={availableDays > 0 && availableDays < days && days !== 99999}
                  onClick={() => setTimeRange(days)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <hr />
        </div>

        {!stats && (
          <div className='eval-data-v2__loading'>Loading activity data…</div>
        )}

        {stats && (
          <>
            <div className='eval-data-v2__stats-grid'>
              <div className='g-list-item stat-card'>
                <div className='stat-card__number'>{stats.totalCompletions.toLocaleString()}</div>
                <div className='stat-card__label'>Total Completions</div>
                <div className='stat-card__sub'>last {timeRange === 365 ? '1 year' : `${timeRange} days`}</div>
              </div>

              <div className='g-list-item stat-card'>
                <div className='stat-card__number'>{stats.uniqueEvalsCount.toLocaleString()}</div>
                <div className='stat-card__label'>Unique Evaluations</div>
                <div className='stat-card__sub'>completed at least once</div>
              </div>

              <div className='g-list-item stat-card'>
                <div className='stat-card__number'>{stats.totalHours.toLocaleString()}</div>
                <div className='stat-card__label'>PD Hours Logged</div>
                <div className='stat-card__sub'>{stats.completionsWithHours.toLocaleString()} completions with hours</div>
              </div>

              <div className='g-list-item stat-card'>
                <div className='stat-card__number'>{stats.cpduCount.toLocaleString()}</div>
                <div className='stat-card__label'>CPDU Completions</div>
                <div className='stat-card__sub'>CPDU-eligible evaluations</div>
              </div>
            </div>

            <div className='g-space-1'></div>

            <div className='eval-data-v2__row'>
              <div className='g-list-item eval-data-v2__chart-card'>
                <h4>Daily Completions</h4>
                <div className='chart-container'>
                  <canvas ref={chartRef}></canvas>
                </div>
              </div>

              <div className='eval-data-v2__side-col'>
                <div className='g-list-item highlight-card'>
                  <div className='highlight-card__label'>Top Evaluation</div>
                  <div className='highlight-card__title'>
                    {stats.mostCommonEval?.title || 'N/A'}
                  </div>
                  <div className='highlight-card__count'>{stats.topEvalCount.toLocaleString()} completions</div>
                  {stats.mostCommonEval?.source && (
                    <div className='meta' style={{ marginTop: '0.35rem' }}>
                      {stats.mostCommonEval.source}
                    </div>
                  )}
                </div>

                <div className='g-space-1'></div>

                {stats.busiestDay && (
                  <div className='g-list-item highlight-card'>
                    <div className='highlight-card__label'>Busiest Day</div>
                    <div className='highlight-card__title'>{stats.busiestDay[0]}</div>
                    <div className='highlight-card__count'>{stats.busiestDay[1].toLocaleString()} completions</div>
                  </div>
                )}
              </div>
            </div>

            <div className='g-space-1'></div>

            <div className='eval-data-v2__row'>
              <div className='g-list-item eval-data-v2__source-card'>
                <h4>Completions by Source</h4>
                <div className='chart-container'>
                  <canvas ref={sourceChartRef}></canvas>
                </div>
              </div>

              <div className='g-list-item eval-data-v2__top-evals'>
                <h4>Top Evaluations</h4>
                <div className='top-evals-list'>
                  {stats.top5.map(({ ev, count, id }, i) => (
                    <div key={id} className='top-eval-item'>
                      <span className='top-eval-rank'>#{i + 1}</span>
                      <span className='top-eval-title'>{ev?.title || id}</span>
                      <span className='top-eval-count'>{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default EvalDataV2;
